import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ─── Slot group definitions ───────────────────────────────────────────────────
// Each tile is a single slot group: image + title + subtitle + alt text +
// destination url. Only `image` + `alt` are bound to photo_gallery via
// sourceBinding, so the media picker (and the group generator below) swap the
// photo and its alt text together while the fixed CTA title/subtitle/url stay
// put. The generator picks the gallery photo using the tile's subtitle as the
// reference for what the image should depict; each tile must land on a
// different photo. (_target has no gallery source, so it stays a hidden,
// independent field.)

const tile1Def: SlotGroupDefinition = {
  label: 'Tile 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    title: { label: 'Title', type: 'text', defaultValue: 'Live Better' },
    subtitle: { label: 'Subtitle', type: 'text', defaultValue: 'Take A Virtual Tour' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
    url: { label: 'Destination URL', type: 'url', defaultValue: '/virtual-tour' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Choose a gallery photo whose subject matches this tile\'s subtitle, "Take A Virtual Tour" — favor a wide shot of the home\'s interior living spaces.',
    context: 'One of four image-callout tiles; each must use a different gallery photo. The tile\'s title and subtitle are fixed labels — use the subtitle as the reference for what the photo should depict.',
    sourceCollection: 'photo_gallery',
  },
};

const tile2Def: SlotGroupDefinition = {
  label: 'Tile 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    title: { label: 'Title', type: 'text', defaultValue: 'Luxury Design' },
    subtitle: { label: 'Subtitle', type: 'text', defaultValue: 'View The Gallery' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
    url: { label: 'Destination URL', type: 'url', defaultValue: '/gallery' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Choose a gallery photo whose subject matches this tile\'s subtitle, "View The Gallery" — favor a photo that showcases interior design or finishes.',
    context: 'One of four image-callout tiles; each must use a different gallery photo. The tile\'s title and subtitle are fixed labels — use the subtitle as the reference for what the photo should depict.',
    sourceCollection: 'photo_gallery',
  },
};

const tile3Def: SlotGroupDefinition = {
  label: 'Tile 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    title: { label: 'Title', type: 'text', defaultValue: 'Local Charm' },
    subtitle: { label: 'Subtitle', type: 'text', defaultValue: 'Explore The Neighborhood' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
    url: { label: 'Destination URL', type: 'url', defaultValue: '/neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Choose a gallery photo whose subject matches this tile\'s subtitle, "Explore The Neighborhood" — favor an exterior, streetscape, or local-area shot.',
    context: 'One of four image-callout tiles; each must use a different gallery photo. The tile\'s title and subtitle are fixed labels — use the subtitle as the reference for what the photo should depict.',
    sourceCollection: 'photo_gallery',
  },
};

const tile4Def: SlotGroupDefinition = {
  label: 'Tile 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    title: { label: 'Title', type: 'text', defaultValue: 'Unmatched Convenience' },
    subtitle: { label: 'Subtitle', type: 'text', defaultValue: 'Explore Our Amenities' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
    url: { label: 'Destination URL', type: 'url', defaultValue: '/amenities' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Choose a gallery photo whose subject matches this tile\'s subtitle, "Explore Our Amenities" — favor a shot of a shared amenity space.',
    context: 'One of four image-callout tiles; each must use a different gallery photo. The tile\'s title and subtitle are fixed labels — use the subtitle as the reference for what the photo should depict.',
    sourceCollection: 'photo_gallery',
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

const TILE_SIZES = '(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw';

export function ImageCallouts() {
  const tile1 = useSlotGroup('imagecallouts.tile1', tile1Def);
  const tile2 = useSlotGroup('imagecallouts.tile2', tile2Def);
  const tile3 = useSlotGroup('imagecallouts.tile3', tile3Def);
  const tile4 = useSlotGroup('imagecallouts.tile4', tile4Def);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section ref={sectionRef} className="reveal-fade-up w-full bg-white px-3 sm:px-5 md:px-6 py-3 sm:py-5 md:py-6">
      {/* White border framing the whole group: the section's white background
          shows through the outer padding while the tiles stay flush against one
          another — the same boxed treatment the Hero and Parallax Banner use. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">

        {/* ── Tile 1 ── */}
        <div className="image-callout group relative h-[320px] md:h-[380px] overflow-hidden">
          <EditableSlotGroup groupPath="imagecallouts.tile1" definition={tile1Def} className="absolute inset-0">
            {tile1.image ? (
              <VibesitesImage
                url={tile1.image}
                alt={tile1.alt || ''}
                sizes={TILE_SIZES}
                className="image-callout-img absolute inset-0 z-0 w-full h-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 z-0 bg-[var(--vibe-color-surface)]" />
            )}

            <div className="image-callout-overlay pointer-events-none absolute inset-0 z-10 bg-black/40" />

            <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center">
              {tile1.title && (
                <h3 className="font-heading text-lg md:text-xl font-bold uppercase tracking-wide text-white leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                  {tile1.title}
                </h3>
              )}
              {tile1.subtitle && (
                <p className="mt-1.5 font-body text-xs md:text-sm font-light text-white/90 drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">
                  {tile1.subtitle}
                </p>
              )}
            </div>

            <a
              href={tile1.url || '#'}
              target={tile1._target || '_self'}
              className="absolute inset-0 z-20"
              aria-label={tile1.title || undefined}
            />
          </EditableSlotGroup>
        </div>

        {/* ── Tile 2 ── */}
        <div className="image-callout group relative h-[320px] md:h-[380px] overflow-hidden">
          <EditableSlotGroup groupPath="imagecallouts.tile2" definition={tile2Def} className="absolute inset-0">
            {tile2.image ? (
              <VibesitesImage
                url={tile2.image}
                alt={tile2.alt || ''}
                sizes={TILE_SIZES}
                className="image-callout-img absolute inset-0 z-0 w-full h-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 z-0 bg-[var(--vibe-color-surface)]" />
            )}

            <div className="image-callout-overlay pointer-events-none absolute inset-0 z-10 bg-black/40" />

            <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center">
              {tile2.title && (
                <h3 className="font-heading text-lg md:text-xl font-bold uppercase tracking-wide text-white leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                  {tile2.title}
                </h3>
              )}
              {tile2.subtitle && (
                <p className="mt-1.5 font-body text-xs md:text-sm font-light text-white/90 drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">
                  {tile2.subtitle}
                </p>
              )}
            </div>

            <a
              href={tile2.url || '#'}
              target={tile2._target || '_self'}
              className="absolute inset-0 z-20"
              aria-label={tile2.title || undefined}
            />
          </EditableSlotGroup>
        </div>

        {/* ── Tile 3 ── */}
        <div className="image-callout group relative h-[320px] md:h-[380px] overflow-hidden">
          <EditableSlotGroup groupPath="imagecallouts.tile3" definition={tile3Def} className="absolute inset-0">
            {tile3.image ? (
              <VibesitesImage
                url={tile3.image}
                alt={tile3.alt || ''}
                sizes={TILE_SIZES}
                className="image-callout-img absolute inset-0 z-0 w-full h-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 z-0 bg-[var(--vibe-color-surface)]" />
            )}

            <div className="image-callout-overlay pointer-events-none absolute inset-0 z-10 bg-black/40" />

            <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center">
              {tile3.title && (
                <h3 className="font-heading text-lg md:text-xl font-bold uppercase tracking-wide text-white leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                  {tile3.title}
                </h3>
              )}
              {tile3.subtitle && (
                <p className="mt-1.5 font-body text-xs md:text-sm font-light text-white/90 drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">
                  {tile3.subtitle}
                </p>
              )}
            </div>

            <a
              href={tile3.url || '#'}
              target={tile3._target || '_self'}
              className="absolute inset-0 z-20"
              aria-label={tile3.title || undefined}
            />
          </EditableSlotGroup>
        </div>

        {/* ── Tile 4 ── */}
        <div className="image-callout group relative h-[320px] md:h-[380px] overflow-hidden">
          <EditableSlotGroup groupPath="imagecallouts.tile4" definition={tile4Def} className="absolute inset-0">
            {tile4.image ? (
              <VibesitesImage
                url={tile4.image}
                alt={tile4.alt || ''}
                sizes={TILE_SIZES}
                className="image-callout-img absolute inset-0 z-0 w-full h-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 z-0 bg-[var(--vibe-color-surface)]" />
            )}

            <div className="image-callout-overlay pointer-events-none absolute inset-0 z-10 bg-black/40" />

            <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center">
              {tile4.title && (
                <h3 className="font-heading text-lg md:text-xl font-bold uppercase tracking-wide text-white leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                  {tile4.title}
                </h3>
              )}
              {tile4.subtitle && (
                <p className="mt-1.5 font-body text-xs md:text-sm font-light text-white/90 drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">
                  {tile4.subtitle}
                </p>
              )}
            </div>

            <a
              href={tile4.url || '#'}
              target={tile4._target || '_self'}
              className="absolute inset-0 z-20"
              aria-label={tile4.title || undefined}
            />
          </EditableSlotGroup>
        </div>

      </div>

      <style>{`
        .image-callout-img {
          transition: transform 0.6s ease;
        }
        .image-callout:hover .image-callout-img {
          transform: scale(1.06);
        }
        .image-callout-overlay {
          transition: background-color 0.3s ease;
        }
        .image-callout:hover .image-callout-overlay {
          background-color: rgba(0, 0, 0, 0.25);
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'ImageCallouts',
  displayName: 'Image Callouts',
  tokens: {},
  slotGroups: {
    'imagecallouts.tile1': tile1Def,
    'imagecallouts.tile2': tile2Def,
    'imagecallouts.tile3': tile3Def,
    'imagecallouts.tile4': tile4Def,
  },
};

(ImageCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ImageCallouts;
